import React from "react";
import { Link } from "react-router-dom";

import useWebsiteProducts from "../hooks/useWebsiteProducts";
import ProductGrid from "../components/products/ProductGrid";

import "../styles/website.css";
import "../styles/products.css";

const ProductsPage = () => {
  const {
    categories,
    products,
    search,
    setSearch,
    categoryId,
    setCategoryId,
    loading,
    error,
  } = useWebsiteProducts();

  const selectedCategory = categories.find(
    (category) => String(category.id) === String(categoryId)
  );

  return (
    <div className="website">
      <main className="products-page">

        {/* =========================================================
            HERO
        ========================================================= */}

        <section className="inner-hero products-hero">
          <div className="products-hero-content">

            <span className="section-label">
              OUR OFFERINGS
            </span>

            <h1>
              Software designed
              <span> for business.</span>
            </h1>

            <p>
              Explore our collection of business solutions designed
              to simplify operations, improve productivity and help
              organizations grow.
            </p>

          </div>
        </section>


        {/* =========================================================
            PRODUCT CATALOG
        ========================================================= */}

        <section className="products-list">

          {/* HEADER */}

          <div className="products-list-header">

            <div className="products-list-heading">

              <span className="products-eyebrow">
                PRODUCT CATALOG
              </span>

              <h2>
                Find the right
                <span> solution.</span>
              </h2>

            </div>

            <p>
              Browse our business software by category or search
              for a specific product.
            </p>

          </div>


          {/* =====================================================
              CATALOG CONTROLS
          ===================================================== */}

          <div className="products-catalog-controls">

            {/* SEARCH */}

            <div className="products-search-wrapper">

              <div className="products-search-icon">
                <svg
                  viewBox="0 0 24 24"
                  width="20"
                  height="20"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <circle cx="11" cy="11" r="7" />
                  <path d="m20 20-4-4" />
                </svg>
              </div>

              <input
                id="product-search"
                type="search"
                className="products-search-input"
                value={search}
                onChange={(event) =>
                  setSearch(event.target.value)
                }
                placeholder="Search products..."
                aria-label="Search products"
              />

              {search && (
                <button
                  type="button"
                  className="products-search-clear"
                  onClick={() => setSearch("")}
                  aria-label="Clear search"
                >
                  ×
                </button>
              )}

            </div>


            {/* CATEGORY FILTER */}

            <div className="products-category-filter">

              <button
                type="button"
                className={
                  `products-category-button ${
                    !categoryId ? "active" : ""
                  }`
                }
                onClick={() => setCategoryId("")}
              >
                <span>All Products</span>

                <small>
                  {products.length}
                </small>
              </button>


              {categories.map((category) => (
                <button
                  key={category.id}
                  type="button"
                  className={
                    `products-category-button ${
                      String(categoryId) === String(category.id)
                        ? "active"
                        : ""
                    }`
                  }
                  onClick={() =>
                    setCategoryId(
                      String(categoryId) === String(category.id)
                        ? ""
                        : category.id
                    )
                  }
                >
                  <span>
                    {category.name}
                  </span>

                  <small>
                    {category.productCount || 0}
                  </small>
                </button>
              ))}

            </div>

          </div>


          {/* =====================================================
              RESULT INFORMATION
          ===================================================== */}

          {!loading && !error && (
            <div className="products-result-bar">

              <div>
                <span className="products-result-label">
                  {selectedCategory
                    ? selectedCategory.name
                    : "All Products"}
                </span>

                {search && (
                  <span className="products-result-search">
                    Search results for "{search}"
                  </span>
                )}
              </div>

              <span className="products-result-count">
                {products.length}{" "}
                {products.length === 1
                  ? "product"
                  : "products"}
              </span>

            </div>
          )}


          {/* =====================================================
              LOADING
          ===================================================== */}

          {loading && (
            <div className="products-loading">

              <div className="product-loading-spinner" />

              <p>
                Loading products...
              </p>

            </div>
          )}


          {/* =====================================================
              ERROR
          ===================================================== */}

          {!loading && error && (
            <div className="products-error">

              <div className="products-error-icon">
                !
              </div>

              <h3>
                Unable to load products
              </h3>

              <p>
                {error}
              </p>

            </div>
          )}


          {/* =====================================================
              PRODUCTS
          ===================================================== */}

          {!loading && !error && (
            <>
              {products.length > 0 ? (
                <ProductGrid products={products} />
              ) : (
                <div className="products-search-empty">

                  <div className="products-empty-icon">
                    <svg
                      viewBox="0 0 24 24"
                      width="32"
                      height="32"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="1.7"
                    >
                      <circle cx="11" cy="11" r="7" />
                      <path d="m20 20-4-4" />
                    </svg>
                  </div>

                  <h3>
                    No products found
                  </h3>

                  <p>
                    We couldn't find any products matching
                    your search or selected category.
                  </p>

                  {(search || categoryId) && (
                    <button
                      type="button"
                      className="products-reset-button"
                      onClick={() => {
                        setSearch("");
                        setCategoryId("");
                      }}
                    >
                      Clear filters
                    </button>
                  )}

                </div>
              )}
            </>
          )}

        </section>


        {/* =========================================================
            CUSTOM SOLUTION CTA
        ========================================================= */}

        <section className="products-cta">

          <div className="products-cta-content">

            <span className="section-label">
              CUSTOM SOLUTIONS
            </span>

            <h2>
              Need something
              <span> specific?</span>
            </h2>

            <p>
              We can build custom software around your business
              requirements and create a solution specifically
              for your organization.
            </p>

          </div>

          <Link
            to="/contact"
            className="white-button products-cta-button"
          >
            Discuss Your Requirement
            <span>→</span>
          </Link>

        </section>

      </main>
    </div>
  );
};

export default ProductsPage;